"use client";

import { useEffect, useState } from "react";
import clsx from "clsx";
import { AnimatePresence, motion } from "motion/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleNotch } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { AGENTIC_STATUS_ICON } from "@/components/ui/agentic-shared/statusTone";
import type { ThinkingStatus } from "@/services/inAppAgent/turnTimeline";

const DEFAULT_LABEL = "Thinking";
/** Show an elapsed counter once a single status has been on screen this long. */
const SHOW_ELAPSED_AFTER_SECONDS = 6;

function useSecondsSince(key: string): number {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    setSeconds(0);
    const startedAt = Date.now();
    const id = window.setInterval(() => setSeconds(Math.floor((Date.now() - startedAt) / 1000)), 1000);
    return () => window.clearInterval(id);
  }, [key]);
  return seconds;
}

/**
 * Shown while the in-app agent turn is active but not streaming text or running tools.
 * Matches the tool-step row layout; the label says what Ani is doing (`turn.status`).
 */
export function ThinkingRow({ status }: { status?: ThinkingStatus | null }) {
  const label = status?.label || DEFAULT_LABEL;
  const detail = status?.detail;
  const statusKey = `${label}\u0000${detail ?? ""}`;
  const seconds = useSecondsSince(statusKey);

  return (
    <motion.div
      className="space-y-1"
      data-testid="chat-thinking-row"
      role="status"
      aria-live="polite"
      initial={{ opacity: 0, y: 2 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
    >
      <div className="flex items-center gap-2 py-0.5 text-xs text-slate-600 dark:text-slate-400">
        <FontAwesomeIcon
          icon={faCircleNotch}
          className={clsx("shrink-0 text-[10px] animate-spin", AGENTIC_STATUS_ICON.running)}
        />
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={statusKey}
            className="min-w-0 truncate"
            initial={{ opacity: 0, y: 3 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -3 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
          >
            <span className="text-shimmer">{label}</span>
            {detail ? <span className="text-slate-500 dark:text-slate-500"> — {detail}</span> : null}
          </motion.span>
        </AnimatePresence>
        {seconds >= SHOW_ELAPSED_AFTER_SECONDS ? (
          <span className="shrink-0 tabular-nums text-slate-400 dark:text-slate-500" aria-hidden="true">
            {seconds}s
          </span>
        ) : null}
      </div>
    </motion.div>
  );
}
